import { getCurrentScope, onScopeDispose } from 'vue';

import { getLogger } from '@platform/logging';

/**
 * @internal Register a scope-disposal cleanup, reporting the misuse when there is
 * no scope to register it against.
 *
 * Several composables here subscribe to a MODULE-LEVEL registry and rely on
 * `onScopeDispose` to unsubscribe. Called outside an effect scope — from a plain
 * module, an event handler, a store action — that cleanup never runs and the
 * subscription is retained for the lifetime of the process. Vue does warn, but its
 * message names neither the composable nor the fix, so the leak reads as noise.
 *
 * The guard exists to make the report actionable, NOT to silence it: skipping the
 * warning would trade a loud leak for a silent one, which is strictly worse. The
 * subscription still leaks; the caller is now told which call leaked it and how to
 * stop it. Routing through `getLogger()` also means the warning reaches the shell's
 * logger in a deployed MFE instead of only a devtools console.
 */
const warnedComposables = new Set<string>();

export function onScopeDisposeOrWarn(cleanup: () => void, composable: string): void {
  if (getCurrentScope()) {
    onScopeDispose(cleanup);
    return;
  }

  // Once per composable per process. Vue's own equivalent warning is compiled out
  // of production builds, so routing this through the host logger makes it the
  // only one of the two that survives into a deployed MFE — and a composable
  // called from a store action or an event handler would otherwise emit a log
  // event on every invocation. The condition is a coding error, reported once;
  // repeating it per call turns a fixable warning into alert noise.
  if (warnedComposables.has(composable)) return;
  warnedComposables.add(composable);

  getLogger().warn(
    `[@abax/platform] ${composable} was called outside an effect scope, so its cleanup ` +
      `cannot be registered and its subscription will be retained for the lifetime of the ` +
      `process. Call it from a component setup, or wrap the call in Vue's effectScope().`,
    { composable },
  );
}
